<script setup lang="ts">
import LiveTrackUsageInspectorContent from '@/components/LiveTrackUsageInspectorContent.vue';
import { buttonVariants } from '@/components/ui/button';
import { useInspectorStore } from '@/stores/inspectorStore';
import { resolveLiveTrackId, liveTrackNameFromRow, trackUsageInspectorTitle } from '@/utils/liveTrackRow';
import { cn } from '@/utils/utils';
import { computed, markRaw } from 'vue';

/**
 * Clickable playlist_count cell: the number opens the track usage inspector, which lists
 * every bucket / custom list (and owning client) that uses the track.
 */
const props = defineProps<{
  field: string;
  dataType: string;
  data: Record<string, unknown>;
}>();

const inspectorStore = useInspectorStore();

function inspectorTitle(data: Record<string, unknown>): string {
  return trackUsageInspectorTitle(liveTrackNameFromRow(data));
}

const liveTrackIdForInspector = computed(() => resolveLiveTrackId(props.data));

const playlistCount = computed(() => {
  const rawCount = props.data[props.field];
  if (typeof rawCount === 'number' && Number.isFinite(rawCount)) {
    return rawCount;
  }
  if (typeof rawCount === 'string' && /^\d+$/.test(rawCount)) {
    return Number(rawCount);
  }
  return null;
});

function onOpenUsageInspector(): void {
  const liveTrackId = liveTrackIdForInspector.value;
  if (liveTrackId == null) {
    return;
  }
  inspectorStore.open(inspectorTitle(props.data), markRaw(LiveTrackUsageInspectorContent), {
    liveTrackId,
  });
}
</script>

<template>
  <div class="min-w-0 w-full max-w-full">
    <button
      v-if="playlistCount != null && liveTrackIdForInspector != null"
      type="button"
      :class="cn(
        buttonVariants({ variant: 'ghost' }),
        'h-auto min-h-0 w-full min-w-0 shrink px-1 py-1 text-left font-normal text-sm tabular-nums underline-offset-2 hover:underline',
      )"
      :data-testid="`playlist-count-${liveTrackIdForInspector}`"
      @click="onOpenUsageInspector"
    >
      {{ playlistCount }}
    </button>
    <span v-else class="px-1 py-1 text-sm tabular-nums">{{ playlistCount ?? '' }}</span>
  </div>
</template>
